import { useCallback, useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Booking } from '../../models/Booking'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import Tooltip from '../app/design/Tooltip'
import { Clickable } from '../common/Clickable'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { ServiceAlertSubtitle } from './ServiceAlertSubtitle'

type Props = {
    booking: Booking
}

export function BookingRequestAlert({ booking }: Props) {
    const client = booking.client ?? new User()
    const { logAndDisplayError } = useErrorHandler()

    const [confirmingState, setConfirmingState] = useState<'idle' | 'confirming' | 'confirmed'>(
        booking.confirmedDate ? 'confirmed' : 'idle'
    )
    const onConfirmBooking = useCallback(async () => {
        setConfirmingState('confirming')
        const result = await RecordFactory.getInstance().confirmBooking(
            booking,
            false /* todo confirm all */,
            false,
            undefined
        )
        if (!result.ok) {
            setConfirmingState('idle')
            logAndDisplayError(result.error)
            return
        }
        setConfirmingState('confirmed')
    }, [booking])

    return (
        <div>
            <Clickable
                className="flex items-center justify-between rounded-lg px-4 py-2"
                href={`/bookings/${booking.id}`}
            >
                <div className="flex items-center gap-4">
                    <ContactAvatar
                        size="md"
                        user={booking.subaccount ?? client}
                        subaccountParentClientName={booking.subaccount?.client?.name}
                        showName={true}
                        truncateName={true}
                        className="cursor-pointer"
                        subtitle={
                            <>
                                <ServiceAlertSubtitle
                                    services={
                                        booking.services.length ? booking.services : booking.lines
                                    }
                                />
                                <Paragraph size="sm" className="text-gray-600">
                                    {booking.date?.format('ddd, MMM Do YYYY')}
                                </Paragraph>
                            </>
                        }
                    />
                </div>

                {booking.job?.person && (
                    <Tooltip label={booking.job?.person.name ?? ''} position="left">
                        <ContactAvatar size="xs" user={booking.job?.person} />
                    </Tooltip>
                )}
            </Clickable>
            <div className="px-4">
                <Button
                    className="mt-2 w-full"
                    intent="secondary"
                    size="small"
                    isLoading={confirmingState === 'confirming'}
                    disabled={confirmingState === 'confirmed'}
                    onClick={onConfirmBooking}
                >
                    {confirmingState === 'confirmed' ? 'Confirmed' : 'Confirm'}
                </Button>
            </div>
        </div>
    )
}
